import { useCallback, useEffect, useRef } from "react";
import { Modal } from "antd";
import ReadOnlyDocumentViewer from "../Document/ReadOnlyDocumentViewer";
import ExtractedTextSearchBar from "../Document/ExtractedTextSearchBar";
import {
  applyPageJump,
  applySearchHighlightWithLadder,
} from "../../utils/citationJump";

/**
 * Read-only PDF viewer modal for Gold Standard Library cards and the
 * Context Analysis quick-look preview.
 *
 * Wraps the shared standalone Apryse reader used by other read-only
 * surfaces. Citation deep-links (BE PR #1794) supply `initialPage` /
 * `highlightQuery` so the modal opens on the cited page with the quote
 * highlighted; the extracted-text search bar lets the user search the
 * AI's clean reading of the document (the PDF's own text layer is
 * often garbage OCR on scanned VA records).
 */
const GoldStandardViewerModal = ({
  open = false,
  onClose,
  documentId = null,
  filename = "",
  category = "",
  fullHeight = false,
  initialPage = null,
  highlightQuery = "",
}) => {
  const title = [filename, category].filter(Boolean).join(" — ") || "Document";
  const viewerHeight = fullHeight ? "calc(100vh - 210px)" : 660;

  // The live Apryse instance, handed up by ReadOnlyDocumentViewer once
  // the document loads. Drives search-hit page jumps. A hit clicked
  // while the viewer is still loading (search returns much faster than
  // a large scanned PDF renders — Bugbot on PR #513) is queued and
  // replayed the moment the instance arrives, not dropped.
  const instanceRef = useRef(null);
  const pendingJumpRef = useRef(null);

  const performJump = useCallback((instance, page, snippet) => {
    if (page != null && page >= 1) {
      applyPageJump(instance, page);
    }
    if (snippet) {
      // Best-effort: the highlight searches the PDF's own text layer,
      // so on garbage-OCR pages it may not land — the page jump still
      // puts the user on the right page.
      applySearchHighlightWithLadder(instance, snippet);
    }
  }, []);

  const handleInstanceReady = useCallback(
    (instance) => {
      instanceRef.current = instance;
      const pending = pendingJumpRef.current;
      if (pending) {
        pendingJumpRef.current = null;
        performJump(instance, pending.page, pending.snippet);
      }
    },
    [performJump]
  );
  // Reset on close AND on document switch while open (Bugbot, second
  // pass): a queued hit from the prior document must not replay on the
  // next one, and the old instance (disposed by the viewer remount)
  // must not make the new document's jumps look ready.
  useEffect(() => {
    instanceRef.current = null;
    pendingJumpRef.current = null;
  }, [open, documentId]);

  const handleJumpToPage = useCallback(
    (page, snippet) => {
      const instance = instanceRef.current;
      if (!instance) {
        pendingJumpRef.current = { page, snippet };
        return;
      }
      performJump(instance, page, snippet);
    },
    [performJump]
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      footer={null}
      width={1100}
      destroyOnClose
      styles={{
        body: {
          height: viewerHeight,
          padding: 0,
          position: "relative",
          display: "flex",
          flexDirection: "column",
        },
      }}
      data-testid="gold-viewer-modal"
    >
      <div style={{ padding: "8px 12px 4px" }}>
        <ExtractedTextSearchBar
          documentId={documentId}
          onJumpToPage={handleJumpToPage}
        />
      </div>
      <div
        style={{ width: "100%", flex: 1, minHeight: 0 }}
        data-testid="gold-viewer-container"
      >
        <ReadOnlyDocumentViewer
          documentId={documentId}
          filename={filename}
          fileType="pdf"
          initialPage={initialPage}
          highlightQuery={highlightQuery}
          onInstanceReady={handleInstanceReady}
        />
      </div>
    </Modal>
  );
};

export default GoldStandardViewerModal;
